import { useAtomCallback } from 'jotai/utils';
import cloneDeep from 'lodash-es/cloneDeep';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLoadingBarState } from '../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import {
  IListItemDataModel,
  ListActionType,
  ListItemsResponse,
} from '../../data-models/list-item.data-model';
import { IListDataModel, ListType } from '../../data-models/list.data-model';
import { companyListStateJ, listsStateJ } from '../state/ListsStateJ';
import { CompanySearchResponseWithRealId } from '../../schemas/CompanySearchResponse.schema';
import { LoadingId, ToastProps } from '../../types';
import {
  createListItem,
  createListItemComment,
  deleteListItemComment,
  fetchListItems,
  fetchLists,
  moveListCompany,
  requestAddListItemToPipeline,
  updateListItemComment,
} from '../queries/MaggieListQueries';
import { getErrorMessage } from '../queryHelpers';

import { MoveListCompanyPayload } from '../../schemas/CompanyList.schema';
import { companyStateJ, getCompanySyncIfResolved } from '../state/CompanyStateJ';
import { getForesightStore } from '../../util/jotai-store';
import { useCurrentUser } from './useCurrentUser';

export function useAddListItemToPipeline() {
  const { actions } = useLoadingBarState();
  const navigate = useNavigate();
  const { pushErrorToast, pushInfoToast } = useToastMessageState();
  const currentUser = useCurrentUser();

  return useAtomCallback(
    useCallback(
      async (get, set, listItem: IListItemDataModel, company: ICompanyDataModel) => {
        const jotaiStore = getForesightStore();
        const companyBeforePipeline = getCompanySyncIfResolved(listItem.companyId);
        const prevSourceList = get(companyListStateJ(listItem.listId));
        let pipelineListId: number | undefined;
        let prevPipelineList: Omit<ListItemsResponse, 'companies'> | null = null;
        let companyOptimisticallyUpdated = false;
        let sourceListOptimisticallyUpdated = false;

        try {
          actions.startLoading(LoadingId.updateListItem);

          jotaiStore.set(companyStateJ(listItem.companyId), (current) => {
            if (!current) {
              return null;
            }
            return { ...current, assignee: currentUser.id };
          });
          companyOptimisticallyUpdated = true;

          set(companyListStateJ(listItem.listId), (current) => {
            if (!current) {
              return current;
            }
            const newItems =
              current.items?.map((item) => {
                if (item.id !== listItem.id) {
                  return item;
                }
                return {
                  ...item,
                  actionType: 'pipeline' as ListActionType,
                  updatedAt: new Date().toISOString(),
                };
              }) ?? [];
            return { items: newItems, versions: current.versions };
          });
          sourceListOptimisticallyUpdated = true;

          const allLists = await get(listsStateJ);
          pipelineListId = allLists?.find((list) => list.name === 'Pipeline')?.id;
          if (pipelineListId) {
            prevPipelineList = get(companyListStateJ(pipelineListId));
            set(companyListStateJ(pipelineListId), (current) => {
              if (!current) {
                return current;
              }
              return {
                ...current,
                items: [...(current.items ?? []), listItem],
                versions: current.versions,
              };
            });
          }

          await requestAddListItemToPipeline(listItem.id);

          const toastConfig: ToastProps = {
            message: `Deal for ${company.name} successfully added to pipeline!`,
            buttonLabel: 'Go to Board',
            buttonAction: () => navigate('/deal-flow'),
          };
          pushInfoToast(toastConfig);
        } catch (err) {
          console.error(err);
          if (sourceListOptimisticallyUpdated) {
            set(companyListStateJ(listItem.listId), prevSourceList);
          }
          if (pipelineListId) {
            set(companyListStateJ(pipelineListId), prevPipelineList);
          }
          if (companyOptimisticallyUpdated) {
            jotaiStore.set(companyStateJ(listItem.companyId), companyBeforePipeline ?? null);
          }
          pushErrorToast({ message: 'An error occurred while trying to create a deal.' });
        } finally {
          actions.stopLoading(LoadingId.updateListItem);
        }
      },
      [actions, navigate, pushErrorToast, pushInfoToast, currentUser.id]
    )
  );
}

export function useListCommentActions() {
  const { pushErrorToast } = useToastMessageState();
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const createItemComment = useAtomCallback(
    useCallback(
      async (get, set, listItem: IListItemDataModel, comment: string) => {
        if (!comment) {
          return;
        }
        setLoading(true);
        const prevLists = new Map<number, Omit<ListItemsResponse, 'companies'> | null>();
        try {
          const { data } = await createListItemComment(listItem.companyId, comment);

          const allLists = await get(listsStateJ);
          const listIds = allLists?.map((list) => list.id) ?? [];
          listIds.forEach((listId) => {
            const current = get(companyListStateJ(listId));
            if (current == null) {
              return;
            }
            prevLists.set(listId, current);
            const updatedItems = current.items.map((item) => {
              if (item.companyId === listItem.companyId) {
                return {
                  ...item,
                  comments: [...(item.comments ?? []), data],
                };
              }
              return item;
            });
            set(companyListStateJ(listId), { ...current, items: updatedItems });
          });
        } catch (err) {
          console.error(err);
          prevLists.forEach((value, listId) => set(companyListStateJ(listId), value));
          const message = getErrorMessage(err, 'Failed to add comment');
          pushErrorToast({ message });
        } finally {
          setLoading(false);
        }
      },
      [pushErrorToast]
    )
  );

  const updateItemComment = useAtomCallback(
    useCallback(
      async (get, set, listItem: IListItemDataModel, commentId: number, comment: string) => {
        if (!comment) {
          return;
        }
        setLoadingId(commentId);
        const prevLists = new Map<number, Omit<ListItemsResponse, 'companies'> | null>();
        try {
          const { data } = await updateListItemComment(commentId, listItem.companyId, comment);

          const allLists = await get(listsStateJ);
          const listIds = allLists?.map((list) => list.id) ?? [];
          listIds.forEach((listId) => {
            const current = get(companyListStateJ(listId));
            if (current == null) {
              return;
            }
            prevLists.set(listId, current);
            const updatedItems = current.items.map((item) => {
              if (item.companyId === listItem.companyId) {
                return {
                  ...item,
                  comments: item.comments?.map((c) => (c.id === data.id ? data : c)),
                };
              }
              return item;
            });
            set(companyListStateJ(listId), { ...current, items: updatedItems });
          });
        } catch (err) {
          console.error(err);
          prevLists.forEach((value, listId) => set(companyListStateJ(listId), value));
          const message = getErrorMessage(err, 'Failed to update comment');
          pushErrorToast({ message });
        } finally {
          setLoadingId(null);
        }
      },
      [pushErrorToast]
    )
  );

  const deleteItemComment = useAtomCallback(
    useCallback(
      async (get, set, listItem: IListItemDataModel, commentId: number) => {
        setLoadingId(commentId);
        try {
          await deleteListItemComment(commentId);

          const allLists = await get(listsStateJ);
          const listIds = allLists?.map((list) => list.id) ?? [];
          listIds.forEach((listId) => {
            const current = get(companyListStateJ(listId));
            if (current == null) {
              return;
            }
            const updatedItems = current.items.map((item) => {
              if (item.companyId === listItem.companyId) {
                return {
                  ...item,
                  comments: item.comments?.filter((c) => c.id !== commentId),
                };
              }
              return item;
            });
            set(companyListStateJ(listId), { ...current, items: updatedItems });
          });
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to delete comment');
          pushErrorToast({ message });
        } finally {
          setLoadingId(null);
        }
      },
      [pushErrorToast]
    )
  );

  return { createItemComment, updateItemComment, deleteItemComment, loading, loadingId };
}

export function useListUploadActions() {
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  const refreshListData = useAtomCallback(
    useCallback(
      async (_get, set, activeList: IListDataModel) => {
        try {
          startLoading(LoadingId.addListItem);
          const [items, lists] = await Promise.all([fetchListItems(activeList.id), fetchLists()]);
          set(companyListStateJ(activeList.id), items);
          set(listsStateJ, lists);
          pushSuccessToast({ message: `Company list ${activeList?.name} uploaded successfully` });
        } catch (error) {
          console.error(error);
          const message = getErrorMessage(error, 'An error occurred while fetching companies');
          pushErrorToast({ message });
        } finally {
          stopLoading(LoadingId.addListItem);
        }
      },
      [pushErrorToast, pushSuccessToast, startLoading, stopLoading]
    )
  );

  const addCompanyToList = useAtomCallback(
    useCallback(
      async (get, set, activeList: IListDataModel, selectedCompany: CompanySearchResponseWithRealId) => {
        const prevCompanyList = get(companyListStateJ(activeList.id));
        const prevLists = await get(listsStateJ);
        try {
          startLoading(LoadingId.addListItem);

          const companyId = Number(selectedCompany.id);
          const createdItem = await createListItem(activeList.id, companyId);
          const allItemsUpdated = await fetchListItems(activeList.id);
          set(companyListStateJ(activeList.id), allItemsUpdated);
          if (prevLists) {
            set(
              listsStateJ,
              prevLists.map((list) => {
                if (list.id === createdItem.listId) {
                  return {
                    ...cloneDeep(list),
                    numberOfItems: list.numberOfItems + 1,
                  };
                }
                return list;
              })
            );
          }

          pushSuccessToast({
            message: `Company ${selectedCompany.fields.name} added to list ${activeList?.name}`,
          });
        } catch (error) {
          console.error(error);
          set(companyListStateJ(activeList.id), prevCompanyList);
          if (prevLists) {
            set(listsStateJ, prevLists);
          }
          const message = getErrorMessage(error, 'An error occurred while adding the company to the list');
          pushErrorToast({ message });
        } finally {
          stopLoading(LoadingId.addListItem);
        }
      },
      [pushErrorToast, pushSuccessToast, startLoading, stopLoading]
    )
  );

  return { refreshListData, addCompanyToList };
}

export function useMoveListCompany() {
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (_get, set, payload: MoveListCompanyPayload) => {
        let success: boolean | undefined;
        try {
          startLoading(LoadingId.addListItem);
          await moveListCompany(payload);
          const [lists, sourceItems, targetItems] = await Promise.all([
            fetchLists(),
            fetchListItems(payload.sourceListId),
            fetchListItems(payload.targetListId),
          ]);
          set(listsStateJ, lists);
          set(companyListStateJ(payload.sourceListId), sourceItems);
          set(companyListStateJ(payload.targetListId), targetItems);
          pushSuccessToast({ message: `Company list moved successfully` });
          success = true;
        } catch (error) {
          console.error(error);
          const message = getErrorMessage(error, 'An error occurred while updating lists');
          pushErrorToast({ message });
        } finally {
          stopLoading(LoadingId.addListItem);
        }
        return success;
      },
      [pushErrorToast, pushSuccessToast, startLoading, stopLoading]
    )
  );
}

export function isMoveAllowed(list: IListDataModel) {
  return list.type === ListType.DISCOVERY;
}
